import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import warehouseService from '../../services/warehouseService';
import { parseApiError } from '../../utils/errorUtils';
import './WarehouseDeletePage.css';
import { ArrowLeft, RotateCcw, Trash2 } from 'lucide-react';

const WarehouseDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [warehouse, setWarehouse] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadWarehouse();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadWarehouse = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await warehouseService.getWarehouseById(parseInt(id || '0'));
      const warehouseData = response?.data?.data || response?.data;
      setWarehouse(warehouseData);
      setLoading(false);
    } catch (err: unknown) {
      setError(parseApiError(err, t('warehouse.failedToLoadWarehouse')).message);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!warehouse) {
      setError('Warehouse data not loaded');
      return;
    }

    setIsDeleting(true);
    setError(null);
    try {
      console.log('Deleting warehouse with ID:', warehouse.id);
      const response = await warehouseService.deleteWarehouse(warehouse.id);
      console.log('Delete response:', response);

      navigate('/organization/warehouses', {
        state: { message: t('warehouse.deletedSuccess') },
      });
    } catch (err: unknown) {
      console.error('Delete error:', err);
      const errorMessage = parseApiError(err, t('warehouse.failedToLoadWarehouse')).message;
      console.error('Error message:', errorMessage);
      setError(errorMessage);
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/organization/warehouses/${warehouse?.id}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!warehouse) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{t('warehouse.failedToLoadWarehouse')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delete-container">
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={handleCancel}
            title={t('common.back')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('warehouse.title')}
          </button>
        </nav>

        <div className="delete-content">
          <div className="delete-card delete-card-main">
            <div className="delete-card-header delete-card-header-warning">
              <Trash2 size={24} strokeWidth={2} className="delete-icon" />
              <h2 className="delete-card-title">{t('common.delete')}</h2>
            </div>

            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                <strong>{t('common.error')}:</strong> {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  style={{ float: 'right' }}
                ></button>
              </div>
            )}

            <div className="delete-card-body">
              <div className="delete-warning-section">
                <p className="delete-warning-text">
                  {t('warehouse.deleteConfirmationMessage')}
                </p>
              </div>

              <div className="delete-warehouse-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('warehouse.code')}</label>
                  <p className="delete-info-value">{warehouse.code}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('warehouse.warehouseName')}</label>
                  <p className="delete-info-value">{warehouse.name}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('warehouse.location')}</label>
                  <p className="delete-info-value">{warehouse.location}</p>
                </div>

                {warehouse.city && (
                  <div className="delete-info-item">
                    <label className="delete-info-label">{t('warehouse.city')}</label>
                    <p className="delete-info-value">{warehouse.city}</p>
                  </div>
                )}
              </div>

              <div className="delete-actions" style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn-cancel"
                  onClick={handleCancel}
                  disabled={isDeleting}
                  title={t('common.cancel')}
                  style={{ minWidth: '100px' }}
                >
                  <RotateCcw size={16} strokeWidth={2} />
                  {t('common.cancel')}
                </button>
                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  title={t('common.delete')}
                  style={{ minWidth: '100px' }}
                >
                  {isDeleting ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('common.delete')}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={2} />
                      {t('common.delete')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WarehouseDeletePage;
